export const enum UIColorSpace { RGB, HSL, HSV };

export interface UIColorData {
    space?: UIColorSpace;
    html?: string;
}

export type UIColor = [number, number, number, number] & UIColorData;

export const $colors = {
    cyan: [85, 170, 165, 1] as UIColor,
    fuchsia: [190, 32, 83, 1] as UIColor,
    orange: [240, 124, 63, 1] as UIColor,
    red: [227, 80, 30, 1] as UIColor,

    category20b: [
        [57, 59, 121, 1],
        [82, 84, 163, 1],
        [107, 110, 207, 1],
        [156, 158, 222, 1],
        [99, 121, 57, 1],
        [140, 162, 82, 1],
        [181, 207, 107, 1],
        [206, 219, 156, 1],
        [140, 109, 49, 1],
        [189, 158, 57, 1],
        [231, 186, 82, 1],
        [231, 203, 148, 1],
        [132, 60, 57, 1],
        [173, 73, 74, 1],
        [214, 97, 107, 1],
        [231, 150, 156, 1],
        [123, 65, 115, 1],
        [165, 81, 148, 1],
        [206, 109, 189, 1],
        [222, 158, 214, 1]
    ] as UIColor[],

    google: [
        [0xFFEBEE, 0xFFCDD2, 0xEF9A9A, 0xE57373, 0xEF5350, 0xF44336, 0xE53935, 0xD32F2F, 0xC62828, 0xB71C1C], //Red
        [0xFCE4EC, 0xF8BBD0, 0xF48FB1, 0xF06292, 0xEC407A, 0xE91E63, 0xD81B60, 0xC2185B, 0xAD1457, 0x880E4F], //Pink
        [0xF3E5F5, 0xE1BEE7, 0xCE93D8, 0xBA68C8, 0xAB47BC, 0x9C27B0, 0x8E24AA, 0x7B1FA2, 0x6A1B9A, 0x4A148C], //Purple
        [0xEDE7F6, 0xD1C4E9, 0xB39DDB, 0x9575CD, 0x7E57C2, 0x673AB7, 0x5E35B1, 0x512DA8, 0x4527A0, 0x311B92], //Deep purple
        [0xE8EAF6, 0xC5CAE9, 0x9FA8DA, 0x7986CB, 0x5C6BC0, 0x3F51B5, 0x3949AB, 0x303F9F, 0x283593, 0x1A237E], //Indigo
        [0xE3F2FD, 0xBBDEFB, 0x90CAF9, 0x64B5F6, 0x42A5F5, 0x2196F3, 0x1E88E5, 0x1976D2, 0x1565C0, 0x0D47A1], //Blue 
        [0xE1F5FE, 0xB3E5FC, 0x81D4FA, 0x4FC3F7, 0x29B6F6, 0x03A9F4, 0x039BE5, 0x0288D1, 0x0277BD, 0x01579B], //Light blue
        [0xE0F7FA, 0xB2EBF2, 0x80DEEA, 0x4DD0E1, 0x26C6DA, 0x00BCD4, 0x00ACC1, 0x0097A7, 0x00838F, 0x006064], //Cyan
        [0xE0F2F1, 0xB2DFDB, 0x80CBC4, 0x4DB6AC, 0x26A69A, 0x009688, 0x00897B, 0x00796B, 0x00695C, 0x004D40], //Teal
        [0xE8F5E9, 0xC8E6C9, 0xA5D6A7, 0x81C784, 0x66BB6A, 0x4CAF50, 0x43A047, 0x388E3C, 0x2E7D32, 0x1B5E20], //Green
        [0xF1F8E9, 0xDCEDC8, 0xC5E1A5, 0xAED581, 0x9CCC65, 0x8BC34A, 0x7CB342, 0x689F38, 0x558B2F, 0x33691E], //Light green
        [0xF9FBE7, 0xF0F4C3, 0xE6EE9C, 0xDCE775, 0xD4E157, 0xCDDC39, 0xC0CA33, 0xAFB42B, 0x9E9D24, 0x827717], //Lime
        [0xFFFDE7, 0xFFF9C4, 0xFFF59D, 0xFFF176, 0xFFEE58, 0xFFEB3B, 0xFDD835, 0xFBC02D, 0xF9A825, 0xF57F17], //Yellow
        [0xFFF8E1, 0xFFECB3, 0xFFE082, 0xFFD54F, 0xFFCA28, 0xFFC107, 0xFFB300, 0xFFA000, 0xFF8F00, 0xFF6F00], //Amber
        [0xFFF3E0, 0xFFE0B2, 0xFFCC80, 0xFFB74D, 0xFFA726, 0xFF9800, 0xFB8C00, 0xF57C00, 0xEF6C00, 0xE65100], //Orange
        [0xFBE9E7, 0xFFCCBC, 0xFFAB91, 0xFF8A65, 0xFF7043, 0xFF5722, 0xF4511E, 0xE64A19, 0xD84315, 0xBF360C], //Deep orange
        [0xEFEBE9, 0xD7CCC8, 0xBCAAA4, 0xA1887F, 0x8D6E63, 0x795548, 0x6D4C41, 0x5D4037, 0x4E342E, 0x3E2723], //Brown
        [0xFAFAFA, 0xF5F5F5, 0xEEEEEE, 0xE0E0E0, 0xBDBDBD, 0x9E9E9E, 0x757575, 0x616161, 0x424242, 0x212121], //Grey
        [0xECEFF1, 0xCFD8DC, 0xB0BEC5, 0x90A4AE, 0x78909C, 0x607D8B, 0x546E7A, 0x455A64, 0x37474F, 0x263238]  //Blue grey
    ]
};

export namespace $color {
    export function HSV(c: UIColor, clone?: boolean): UIColor {
        if(c.space === UIColorSpace.HSV) {
            if(clone) {
                c = c.slice() as UIColor;
                c.space = UIColorSpace.HSV;
            }
            return c;
        } else if(c.space === UIColorSpace.HSL) {
            let hsv = c.slice() as UIColor;
            let sat = c[1] * (c[2] < 0.5 ? c[2] : 1 - c[2]);
            hsv[2] = sat + c[2];
            hsv[1] = 2 * sat / (hsv[2]);
            hsv.space = UIColorSpace.HSV;
            return hsv;
        } else {
            const r = c[0] / 255, g = c[1] / 255, b = c[2] / 255;
            const min = Math.min(r, g, b);
            const max = Math.max(r, g, b);

            let h = 0;
            let s = max - min;
            let v = max;

            if(s) {
                if(g === max)
                    h = (b - r) / s + 2;
                else if(b === max)
                    h = (r - g) / s + 4;
                else if(g < b)
                    h = (g - b) / s + 6;
                else
                    h = (g - b) / s;

                h *= 60;
                s /= v;
            }

            const hsv: UIColor = [h, s, v, c[3]];
            hsv.space = UIColorSpace.HSV;
            return hsv;
        }
    }

    export function clone(c: UIColor): UIColor {
        const n = c.slice() as UIColor;
        n.space = UIColorSpace.HSL;
        return n;
    }

    export function HSL(c: UIColor, clone?: boolean): UIColor {
        if(c.space === UIColorSpace.HSL) {
            if(clone) {
                c = c.slice() as UIColor;
                c.space = UIColorSpace.HSL;
            }

            return c;
        } else if(c.space === UIColorSpace.HSV) {
            let hsl = c.slice() as UIColor;
            let v = (2 - c[1]) * c[2];
            hsl[1] = c[1] * c[2] / (v < 1 ? v : 2 - v);
            hsl[2] = v / 2;
            hsl.space = UIColorSpace.HSL;
            return hsl;
        } else {
            const r = c[0] / 255, g = c[1] / 255, b = c[2] / 255;
            const min = Math.min(r, g, b);
            const max = Math.max(r, g, b);

            let h = 0;
            let s = max - min;
            let l = (max + min) / 2;

            if(s) {
                if(g === max)
                    h = (b - r) / s + 2;
                else if(b === max)
                    h = (r - g) / s + 4;
                else if(g < b)
                    h = (g - b) / s + 6;
                else
                    h = (g - b) / s;

                h *= 60;
                s /= 1 - Math.abs(2 * l - 1);
            }

            const hsl: UIColor = [h, s, l, c[3]];
            hsl.space = UIColorSpace.HSL;
            return hsl;
        }
    }

    export function RGB(c: UIColor): UIColor {
        if(!c.space) return c;

        let ch: number, x: number, p: number;

        let h = c[0] / 60;

        if(c.space == UIColorSpace.HSL) {
            let s = c[1], l = c[2];
            ch = (1 - Math.abs(2 * l - 1)) * s * 255;
            x = ch * (1 - Math.abs(h % 2 - 1))
            p = l * 255 - ch / 2;
            ch += p;
            x += p;
        } else { // HSV
            let s = c[1], v = c[2];
            ch = v * 255;
            p = (1 - s) * ch;
            x = p + (ch - p) * (1 - Math.abs(h % 2 - 1));
        }

        let r: number, g: number, b: number;

        switch(Math.floor(h)) {
            default: case 0: r = ch, g = x, b = p; break;
            case 1: r = x, g = ch, b = p; break;
            case 2: r = p, g = ch, b = x; break;
            case 3: r = p, g = x, b = ch; break;
            case 4: r = x, g = p, b = ch; break;
            case 5: r = ch, g = p, b = x; break;
        }

        return [Math.round(r), Math.round(g), Math.round(b), c[3]];
    }

    export function HTML(c: UIColor): string {
        if(c.space === UIColorSpace.HSL) {
            return `hsla(${c[0].toFixed(1)},${(c[1] * 100).toFixed(1)}%,${(c[2] * 100).toFixed(1)}%,${c[3].toFixed(2)})`
        } else if(c.space === UIColorSpace.HSV) {
            let v = (2 - c[1]) * c[2];
            let s = c[1] * c[2] / (v < 1 ? v : 2 - v);
            return `hsla(${c[0].toFixed(1)},${(s * 100).toFixed(1)}%,${(v * 50).toFixed(1)}%,${c[3].toFixed(2)})`
        } else {
            return `rgba(${c[0].toFixed(0)},${c[1].toFixed(0)},${c[2].toFixed(0)},${c[3].toFixed(2)})`
        }
    }

    export function HEX(c: UIColor): string {
        const rgb = RGB(c);
        return '#' + ((1 << 24) + (rgb[0] << 16) + (rgb[1] << 8) + rgb[2]).toString(16).slice(1);
    }

    export function maybe(c: string | number | undefined): UIColor | undefined {
        if(typeof c === 'string') {
            if(c.charAt(0) === '#') {
                const m = parseInt(c.substr(1), 16);
                if(c.length === 4) {
                    return [(m >> 8) * 17, ((m >> 4) & 0xf) * 17, (m & 0xf) * 17, 1];
                } else if(c.length === 7) {
                    return [m >> 16, (m >> 8) & 0xff, m & 0xff, 1];
                }
            }
        } else if(typeof c === 'number') {
            const a = c >> 24;
            return [(c >> 16) & 0xff, (c >> 8) & 0xff, c & 0xff, a ? a / 255 : 1];
        }
        return undefined;
    }

    export function make(c: string | number): UIColor {
        return maybe(c) || [0, 0, 0, 1];
    }

    export function mix(c1: UIColor, c2: UIColor, m2: number = 0.5): UIColor {
        const m1 = 1 - m2;
        const c = [c1[0] * m1 + c2[0] * m2, c1[1] * m1 + c2[1] * m2, c1[2] * m1 + c2[2] * m2, c1[3] * m1 + c2[3] * m2] as UIColor;
        c.space = c1.space;
        return c;
    }

    export function shades(c: UIColor, n: number, spread: number = 0.2): UIColor[] {
        if(n === 0) return [];
        if(n === 1) return [c];

        const hsl = HSL(c, true);

        const colors: UIColor[] = [];
        const min = Math.max(0, hsl[2] - spread / 2);
        const step = Math.min(spread, 1 - min) / (n - 1);

        for(let i = 0; i < n; i++) {
            hsl[2] = min + i * step;
            colors.push(RGB(hsl));
        }

        return colors;
    }

    export function isLight(c: UIColor) {
        return 30 * c[0] + 58 * c[1] + 12 * c[2] > 17850; //intensity > 0.7
    }

    export function isVeryLight(c: UIColor) {
        return 30 * c[0] + 58 * c[1] + 12 * c[2] > 22950; //intensity > 0.9
    }
}